import { Event, EventType, EventTypes } from '@cashier-integration/model';

import { PaymentFormField } from '@cashier/cashier-model';

export function createEvent<T = any>(type: EventType, payload?: T): Event<T> {
  return {
    type,
    payload,
  };
}

/**
 * Create an outgoing event (to host)
 */
export function createOutgoingEvent<T = any>(
  type: EventType,
  payload?: T,
): Event<T> {
  return createEvent(type, payload);
}

export const EventFactory = {
  // UI Events
  scrollTop: () => createOutgoingEvent(EventTypes.SCROLL_TOP),

  changeCashierHeight: (height: number) => {
    return createOutgoingEvent(EventTypes.CASHIER_HEIGHT_CHANGE, height);
  },

  onFieldFocused: (fieldName: string) =>
    createOutgoingEvent(EventTypes.ON_FIELD_FOCUSED, fieldName),

  onAmountChosen: (amount: string) =>
    createOutgoingEvent(EventTypes.ON_AMOUNT_CHOSEN, amount),

  onValidationFailed: (fieldName: string, errorMessage: string) =>
    createOutgoingEvent(EventTypes.ON_VALIDATION_FAILED, {
      fieldName,
      errorMessage,
    }),

  // Transaction Events
  processingResult: (transactionId: string, state: string, amount: string) =>
    createOutgoingEvent(EventTypes.PROCESSING_RESULT, {
      transactionId,
      state,
      amount,
    }),

  transactionFailed: (transactionId: string, state: string, amount: string) =>
    createOutgoingEvent(EventTypes.TRANSACTION_FAILED, {
      transactionId,
      state,
      amount,
    }),

  transactionCanceled: (transactionId: string, state: string, amount: string) =>
    createOutgoingEvent(EventTypes.TRANSACTION_CANCELED, {
      transactionId,
      state,
      amount,
    }),

  paymentInitiated: (
    event: string,
    value: string | boolean | string[],
    paymentMethod: string,
    currency: string,
  ) =>
    createOutgoingEvent(EventTypes.PAYMENT_INITIATED, {
      event,
      value,
      payment_method: paymentMethod,
      currency,
    }),

  depositError: (
    value: string | boolean | string[],
    currency: string,
    paymentMethod: string,
    error: string,
    transactionId: string,
    code?: string,
  ): Event =>
    createOutgoingEvent(EventTypes.DEPOSIT_ERROR, {
      event: 'deposit',
      value,
      currency,
      payment_method: paymentMethod,
      error,
      transactionId,
      ...(code && { code }),
    }),

  withdrawError: (
    value: string | boolean | string[],
    currency: string,
    paymentMethod: string,
    error: string,
    transactionId: string,
    code?: string,
  ) =>
    createOutgoingEvent(EventTypes.WITHDRAW_ERROR, {
      event: 'withdraw',
      value,
      currency,
      payment_method: paymentMethod,
      error,
      transactionId,
      ...(code && { code }),
    }),

  // Form Events
  submitForm: (url: string, method: string, parameters: Record<string, any>) =>
    createOutgoingEvent(EventTypes.SUBMIT_FORM, {
      url,
      method,
      parameters,
    }),

  submitProviderForm: (
    method: string,
    url: string,
    data: Record<string, any>,
  ) =>
    createOutgoingEvent(EventTypes.SUBMIT_PROVIDER_FORM, {
      method,
      url,
      data,
    }),

  // Payment Lifecycle Events
  paymentFormReady: (status: number, data: Record<string, any>) =>
    createOutgoingEvent(EventTypes.ON_PAYMENT_FORM_READY, { status, data }),

  paymentAttributesChanged: (attributes: any[]) =>
    createOutgoingEvent(EventTypes.ON_PAYMENT_ATTRIBUTES_CHANGED, attributes),

  paymentDataSubmitted: () =>
    createOutgoingEvent(EventTypes.ON_PAYMENT_DATA_SUBMITTED),

  paymentDataSubmitDeclined: () =>
    createOutgoingEvent(EventTypes.ON_PAYMENT_DATA_SUBMIT_DECLINED),

  paymentDataPreProcessed: () =>
    createOutgoingEvent(EventTypes.ON_PAYMENT_DATA_PRE_PROCESSED),

  paymentDataConfirmationInitiated: () =>
    createOutgoingEvent(EventTypes.ON_PAYMENT_DATA_CONFIRMATION_INITIATED),

  paymentConfirmed: () => createOutgoingEvent(EventTypes.ON_PAYMENT_CONFIRMED),

  paymentProcessed: (
    status: number,
    data: { redirectUrl?: string; html?: string },
  ) =>
    createOutgoingEvent(EventTypes.ON_PAYMENT_PROCESSED, {
      status,
      data,
    }),

  paymentProcessingStarted: () =>
    createOutgoingEvent(EventTypes.ON_PAYMENT_PROCESSING_STARTED),

  showMessage: (content: string) =>
    createOutgoingEvent(EventTypes.SHOW_MESSAGE, { content }),

  // Navigation Events
  changePaymentMethod: (type: string) =>
    createOutgoingEvent(EventTypes.CHANGE_PAYMENT_METHOD, {
      type,
    }),

  getBackToPaymentMethods: () =>
    createOutgoingEvent(EventTypes.GET_BACK_TO_PAYMENT_METHODS),

  cashierClosed: (status: string, message: string) =>
    createOutgoingEvent(EventTypes.ON_CASHIER_CLOSED, {
      status,
      message,
    }),

  // Bonus Events
  bonusSelected: (bonusCode: string) =>
    createOutgoingEvent(EventTypes.ON_BONUS_SELECTED, bonusCode),

  bonusCalculated: (amount: string, bonus: string | null) =>
    createOutgoingEvent(EventTypes.ON_BONUS_CALCULATED, {
      amount,
      bonus,
    }),

  // Customer Events
  customerBalance: (balance: number) =>
    createOutgoingEvent(EventTypes.CUSTOMER_BALANCE, balance),

  // Retry Events
  newPaymentInitiated: () =>
    createOutgoingEvent(EventTypes.NEW_PAYMENT_INITIATED),

  paymentRetried: (amount: string, transactionId?: string) =>
    createOutgoingEvent(EventTypes.PAYMENT_RETRIED, {
      amount,
      ...(transactionId && { transactionId }),
    }),

  // Field Events
  cashierMessage: (payload?: {
    task?: string;
    paymentAttributes?: PaymentFormField[];
    isAccountUsed?: boolean;
  }) => createOutgoingEvent(EventTypes.MESSAGE_TO_LEADER_IFRAME, payload),
};
